import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import client, { mediaUrl } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { ILLUS } from "../../lib/data";
import AdminLayout from "./AdminLayout";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

const STATUSES = ["Draft", "Published", "Archived"];

// Field configs per collection
const CONFIGS = {
  activities: {
    title: "Areas of Focus",
    primary: "title",
    fields: [
      { key: "title", label: "Title", type: "text", required: true },
      { key: "description", label: "Short Description", type: "textarea", required: true },
      { key: "illustration", label: "Illustration", type: "illustration" },
      { key: "order", label: "Display Order", type: "number" },
    ],
  },
  projects: {
    title: "Projects",
    primary: "name",
    fields: [
      { key: "name", label: "Project Name", type: "text", required: true },
      { key: "shortDescription", label: "Short Description", type: "textarea" },
      { key: "detailedDescription", label: "Detailed Description", type: "textarea" },
      { key: "category", label: "Category", type: "text" },
      { key: "image", label: "Image", type: "upload" },
      { key: "location", label: "Location", type: "text" },
      { key: "objectives", label: "Objectives", type: "textarea" },
      { key: "facilities", label: "Facilities", type: "textarea" },
      { key: "eligibility", label: "Eligibility", type: "text" },
      { key: "contactInformation", label: "Contact Information", type: "text" },
      { key: "capacity", label: "Capacity", type: "text" },
      { key: "startDate", label: "Start Date", type: "text" },
      { key: "endDate", label: "Completion Date", type: "text" },
      { key: "order", label: "Display Order", type: "number" },
    ],
  },
  news: {
    title: "News",
    primary: "title",
    fields: [
      { key: "title", label: "Title", type: "text", required: true },
      { key: "category", label: "Category", type: "text" },
      { key: "date", label: "Date", type: "text" },
      { key: "image", label: "Image", type: "upload" },
      { key: "summary", label: "Summary", type: "textarea" },
      { key: "content", label: "Full Article", type: "textarea" },
      { key: "order", label: "Display Order", type: "number" },
    ],
  },
  events: {
    title: "Events",
    primary: "title",
    fields: [
      { key: "title", label: "Title", type: "text", required: true },
      { key: "category", label: "Category", type: "text" },
      { key: "date", label: "Date", type: "text" },
      { key: "image", label: "Image", type: "upload" },
      { key: "summary", label: "Summary", type: "textarea" },
      { key: "content", label: "Full Details", type: "textarea" },
      { key: "order", label: "Display Order", type: "number" },
    ],
  },
  gallery: {
    title: "Gallery",
    primary: "title",
    fields: [
      { key: "title", label: "Title / Caption", type: "text" },
      { key: "image", label: "Image", type: "upload", required: true },
      { key: "category", label: "Category", type: "gallerycat" },
      { key: "order", label: "Display Order", type: "number" },
    ],
  },
  leadership: {
    title: "Leadership",
    primary: "name",
    fields: [
      { key: "name", label: "Name", type: "text", required: true },
      { key: "position", label: "Position", type: "text", required: true },
      { key: "photo", label: "Photo", type: "upload" },
      { key: "order", label: "Display Order", type: "number" },
    ],
  },
  initiatives: {
    title: "Initiatives",
    primary: "title",
    fields: [
      { key: "title", label: "Initiative Title", type: "text", required: true },
      { key: "shortDescription", label: "Short Description", type: "textarea" },
      { key: "description", label: "Full Description", type: "textarea" },
      { key: "category", label: "Category / Area", type: "text" },
      { key: "image", label: "Featured Image", type: "upload" },
      { key: "startDate", label: "Start Date", type: "text" },
      { key: "order", label: "Display Order", type: "number" },
    ],
  },
};

const GALLERY_CATS = ["Activities", "Religious & Cultural", "Community Service", "Education", "Health", "Senior Citizen Welfare", "Old Age Home & Elder Care", "Events", "Other"];

export default function ResourceManager({ collection }) {
  const cfg = CONFIGS[collection];
  const { user } = useAuth();
  const canEdit = ["super_admin", "content_admin"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // object or null
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = () => client.get(`/admin/cms/${collection}`).then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [collection]);

  const openNew = () => setEditing({ status: "Draft", order: items.length });
  const handleUpload = async (e, key) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await client.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setEditing((cur) => ({ ...cur, [key]: data.url }));
      toast.success("Image uploaded");
    } catch { toast.error("Upload failed. Please try a smaller JPG/PNG."); }
  };
  const save = async () => {
    for (const f of cfg.fields) {
      if (f.required && !editing[f.key]) { toast.error(`${f.label} is required.`); return; }
    }
    try {
      if (editing.id) await client.put(`/admin/cms/${collection}/${editing.id}`, editing);
      else await client.post(`/admin/cms/${collection}`, editing);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch { toast.error("Could not save."); }
  };
  const remove = async (id) => {
    await client.delete(`/admin/cms/${collection}/${id}`);
    toast.success("Deleted");
    setConfirmDelete(null);
    load();
  };

  return (
    <AdminLayout title={cfg.title}>
      <div className="mb-5 flex justify-end">
        <button onClick={openNew} data-testid="resource-add" className="inline-flex items-center gap-2 bg-maroon px-5 py-2.5 text-xs uppercase tracking-wide text-ivory hover:bg-maroon-dark">
          <Plus size={16} /> Add New
        </button>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {items.map((it) => (
          <div key={it.id} className="rounded-md border border-gold/20 bg-white p-5" data-testid={`resource-${it.id}`}>
            {it.illustration && ILLUS[it.illustration] && <img src={ILLUS[it.illustration]} alt="" className="mb-3 h-24 w-full object-contain" />}
            {it.image && <img src={mediaUrl(it.image)} alt="" className="mb-3 h-24 w-full rounded-sm object-cover" />}
            {it.photo && <img src={mediaUrl(it.photo)} alt="" className="mb-3 h-16 w-16 rounded-full object-cover" />}
            <div className="flex items-start justify-between gap-2">
              <h3 className="font-medium text-maroon">{it[cfg.primary] || "Untitled"}</h3>
              <span className={`shrink-0 rounded-sm px-2 py-0.5 text-[10px] uppercase ${it.status === "Published" ? "bg-maroon text-ivory" : "bg-ivory text-ink-muted"}`}>{it.status}</span>
            </div>
            {it.position && <p className="text-xs text-gold-dark">{it.position}</p>}
            {(it.description || it.summary || it.shortDescription) && <p className="mt-2 line-clamp-2 text-sm text-ink-muted">{it.description || it.summary || it.shortDescription}</p>}
            <div className="mt-4 flex gap-2">
              {canEdit ? (
                <>
                  <button onClick={() => setEditing(it)} className="inline-flex items-center gap-1 text-xs text-maroon hover:underline" data-testid={`edit-${it.id}`}><Pencil size={13} /> Edit</button>
                  <button onClick={() => setConfirmDelete(it)} className="inline-flex items-center gap-1 text-xs text-destructive hover:underline" data-testid={`delete-${it.id}`}><Trash2 size={13} /> Delete</button>
                </>
              ) : (
                <span className="text-xs text-ink-muted">Read-only</span>
              )}
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-sm text-ink-muted">No entries yet. Click “Add New”.</p>}
      </div>

      {/* Editor modal */}
      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setEditing(null)}>
          <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-md bg-white p-7" onClick={(e) => e.stopPropagation()} data-testid="resource-editor">
            <h2 className="mb-4 font-serif text-2xl text-maroon">{editing.id ? "Edit" : "Add"} {cfg.title}</h2>
            <div className="space-y-4">
              {cfg.fields.map((f) => (
                <div key={f.key}>
                  <Label>{f.label}{f.required && " *"}</Label>
                  {f.type === "textarea" ? (
                    <Textarea className="mt-1.5" rows={3} value={editing[f.key] || ""} onChange={(e) => setEditing({ ...editing, [f.key]: e.target.value })} data-testid={`field-${f.key}`} />
                  ) : f.type === "number" ? (
                    <Input className="mt-1.5" type="number" value={editing[f.key] ?? 0} onChange={(e) => setEditing({ ...editing, [f.key]: Number(e.target.value) })} data-testid={`field-${f.key}`} />
                  ) : f.type === "illustration" ? (
                    <Select value={editing[f.key] || ""} onValueChange={(v) => setEditing({ ...editing, [f.key]: v })}>
                      <SelectTrigger className="mt-1.5" data-testid={`field-${f.key}`}><SelectValue placeholder="Select illustration" /></SelectTrigger>
                      <SelectContent>{Object.keys(ILLUS).map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : f.type === "gallerycat" ? (
                    <Select value={editing[f.key] || ""} onValueChange={(v) => setEditing({ ...editing, [f.key]: v })}>
                      <SelectTrigger className="mt-1.5" data-testid={`field-${f.key}`}><SelectValue placeholder="Select category" /></SelectTrigger>
                      <SelectContent>{GALLERY_CATS.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : f.type === "upload" ? (
                    <div className="mt-1.5">
                      {editing[f.key] && <img src={mediaUrl(editing[f.key])} alt="preview" className="mb-2 h-28 w-full rounded-sm border border-gold/20 object-cover" />}
                      <input type="file" accept="image/*" onChange={(e) => handleUpload(e, f.key)} data-testid={`field-${f.key}`} className="block w-full text-sm text-ink-muted file:mr-3 file:border-0 file:bg-maroon file:px-4 file:py-2 file:text-xs file:uppercase file:text-ivory" />
                    </div>
                  ) : (
                    <Input className="mt-1.5" value={editing[f.key] || ""} onChange={(e) => setEditing({ ...editing, [f.key]: e.target.value })} data-testid={`field-${f.key}`} />
                  )}
                </div>
              ))}
              <div>
                <Label>Status</Label>
                <Select value={editing.status || "Draft"} onValueChange={(v) => setEditing({ ...editing, status: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="field-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="mt-6 flex gap-3">
              <button onClick={save} data-testid="resource-save" className="flex-1 bg-maroon px-5 py-3 text-xs uppercase tracking-wide text-ivory hover:bg-maroon-dark">Save</button>
              <button onClick={() => setEditing(null)} className="flex-1 border border-gold/40 px-5 py-3 text-xs uppercase tracking-wide text-maroon">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-sm rounded-md bg-white p-7 text-center" data-testid="confirm-delete">
            <Trash2 className="mx-auto text-destructive" size={28} />
            <h3 className="mt-3 text-lg font-medium text-maroon">Delete this entry?</h3>
            <p className="mt-2 text-sm text-ink-muted">This action cannot be undone.</p>
            <div className="mt-5 flex gap-3">
              <button onClick={() => remove(confirmDelete.id)} className="flex-1 bg-destructive px-4 py-2.5 text-xs uppercase text-white" data-testid="confirm-delete-yes">Delete</button>
              <button onClick={() => setConfirmDelete(null)} className="flex-1 border border-gold/40 px-4 py-2.5 text-xs uppercase text-maroon">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
